import { GetServerSideProps, NextPage } from 'next'
import nextI18nextConfig from '~/next-i18next.config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { DependencyInjector } from '~/injector/dependency.injector'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { ApiTrophyResponseInterface } from '~/modules/Trophies/Application/ApiTrophyResponseInterface'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { GetRelatedTrophies } from '~/modules/Trophies/Application/GetRelatedTrophies'
import { GetTrophy } from '~/modules/Trophies/Application/GetTrophy'
import { RelatedTrophies } from '~/components/RelatedTrophies/RelatedTrophies'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import { TrophyDetail } from '~/components/TrophyDetail/TrophyDetail'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'
import { TrophyDetailComponentTranslatorDto }
  from '~/modules/Trophies/Infrastructure/TrophyDetailComponentTranslatorDto'
import { TrophyMemories } from '~/modules/TrophyMemories/Infrastructure/Components/TrophyMemories'
import { getServerSession } from 'next-auth/next'
import { authOptions } from '~/pages/api/auth/[...nextauth]'
import { ReactElement, useEffect } from 'react'
import { SegmentAnalyticsService } from '~/modules/Analytics/Infrastructure/SegmentAnalyticsService'

interface Props {
  htmlPageMetaContextProps: HtmlPageMetaContextProps
  trophy: ApiTrophyResponseInterface
  relatedTrophies: ApplicationTrophyDto[]
  language: string
  owner: boolean
  backlink?: boolean
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const { trophyId, backlink } = context.query
  const trophyRepository: TrophyRepositoryInterface = DependencyInjector.getInstance()
    .resolve('TrophyRepositoryInterface')

  const session = await getServerSession(context.req, context.res, authOptions)

  if (trophyId) {
    try {
      const relatedTrophies: ApplicationTrophyDto[] = await (
        new GetRelatedTrophies(trophyRepository)
      ).run(trophyId.toString())

      const trophy: ApiTrophyResponseInterface = await (new GetTrophy(trophyRepository)).run(trophyId.toString(), true)

      const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

      let owner = false

      if (session && session.user) {
        owner = session.user.id === trophy.userId
      }

      return {
        props: {
          htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
          trophy: JSON.parse(JSON.stringify(trophy)),
          relatedTrophies: JSON.parse(JSON.stringify(relatedTrophies)),
          language: locale,
          owner,
          ...(backlink !== undefined && { backlink: true }),
          ...await serverSideTranslations(locale, [
            'user_tos',
            'app_menu',
            'footer',
            'trophy_details',
            'related_trophies',
            'trophies',
            'trophy_memories',
            'wallet_error_modal',
            'backlink',
          ]),
        },
      }
    } catch (exception: unknown) {
      console.error(exception)
    }
  }

  return {
    notFound: true,
  }
}

const TrophyDetailsPage: NextPage<Props> = (props) => {
  const trophyComponentDto = TrophyDetailComponentTranslatorDto.fromApplication(props.trophy)
  const { language } = props
  let relatedTrophies

  useEffect(() => {
    async function sendProductViewedEvent () {
      const analytics = new SegmentAnalyticsService(window.analytics)

      await analytics.trackProductViewed({
        category: 'trophy',
        collection: trophyComponentDto.competition,
        organizer: trophyComponentDto.organizer.name,
        productId: trophyComponentDto.id,
        language,
      })
    }

    sendProductViewedEvent().catch(() => {
      console.error(`Failed to send Product viewed event for trophy with ID ${trophyComponentDto.id}`)
    })
  }, [
    trophyComponentDto.id,
    trophyComponentDto.competition,
    trophyComponentDto.organizer.name,
    language,
  ])

  if (!props.owner && props.relatedTrophies.length > 0) {
    const trophies =
      props.relatedTrophies.map((trophy) =>
        ComponentTrophyDtoTransformer.fromApplication(trophy)
      )

    relatedTrophies = <RelatedTrophies trophies={ trophies } />
  }

  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(props.trophy.name, props.trophy.description, props.trophy.image)
  ).getProperties()
  const htmlPageMetaProps = { ...props.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  let memories: ReactElement | null = null

  if (trophyComponentDto.memories.length > 0 || props.owner) {
    memories = (
      <TrophyMemories
        trophy={ trophyComponentDto }
        isOwner={ props.owner }
      />
    )
  }

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <TrophyDetail
        trophyDetailDto={ trophyComponentDto }
        backlink={ props.backlink }
      />

      { memories }

      { relatedTrophies }
    </>
  )
}

export default TrophyDetailsPage
